import { AmbientGlow, GlassCard, ScrollReveal } from '@/components/design-system';
import EarningsHistoryCard from '@/components/household/EarningsHistoryCard';
import HouseholdProfileCard from '@/components/household/HouseholdProfileCard';
import ImpactSummarySection from '@/components/household/ImpactSummarySection';
import WalletBalanceCard from '@/components/household/WalletBalanceCard';
import PageShell from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { subscribeUsers, type UsersResponse } from '@/lib/collections/users';
import { useAuth } from '@pooflabs/web';
import { Recycle, Wallet } from 'lucide-react';

/**
 * Household Wallet / Profile hub: real impact summary, live devnet wallet
 * balance, real onchain earnings history, and an editable profile card — all
 * driven by the connected household's own data.
 */
export function WalletPage() {
  const { user, login } = useAuth();

  const { data: profile, loading: profileLoading } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  const { data: listings } = useRealtimeData<ListingsResponse[]>(
    subscribeManyListings,
    !!user?.address,
    `created by ${user?.address ?? ''}, newest first`
  );

  if (!user?.address) {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="amber" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Wallet className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Household Wallet</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Connect to get started</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Connect your wallet to see your impact, balance, earnings, and profile.
              </p>
              <Button
                onClick={() => login()}
                size="lg"
                className="mt-6 w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
              >
                Connect wallet
              </Button>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  if (!profileLoading && profile && profile.role !== 'household') {
    return (
      <PageShell>
        <div className="container flex min-h-[70vh] items-center justify-center">
          <ScrollReveal className="relative w-full max-w-md">
            <AmbientGlow color="primary" size={360} position="top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            <GlassCard padding="lg" className="text-center">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
                <Recycle className="h-6 w-6 text-primary" strokeWidth={2} />
              </div>
              <span className="text-overline">Household Wallet</span>
              <h1 className="font-heading mt-2 text-2xl font-bold text-foreground">Household-only space</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                This wallet dashboard is for households recovering plastic. Your profile is set up as{' '}
                <span className="font-medium text-foreground">{profile.role}</span>.
              </p>
            </GlassCard>
          </ScrollReveal>
        </div>
      </PageShell>
    );
  }

  const completed = (listings ?? [])
    .filter((l) => l.createdBy === user.address && l.status === 'completed')
    .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));

  return (
    <PageShell>
      <div className="container relative py-8">
        <AmbientGlow color="primary" size={420} position="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <AmbientGlow color="amber" size={380} position="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />

        <ScrollReveal className="relative mb-8">
          <span className="text-overline">Household Wallet</span>
          <h1 className="font-heading mt-1 text-3xl font-bold text-foreground sm:text-4xl">
            Your impact, your earnings, your wallet
          </h1>
        </ScrollReveal>

        <div className="relative mb-8">
          <ImpactSummarySection completedListings={completed} />
        </div>

        <div className="relative grid gap-6 lg:grid-cols-2 lg:items-start">
          <ScrollReveal delay={0.06}>
            <WalletBalanceCard walletAddress={user.address} />
          </ScrollReveal>
          <ScrollReveal delay={0.12}>
            <EarningsHistoryCard listings={completed} />
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.18} className="relative mt-6">
          <HouseholdProfileCard userAddress={user.address} profile={profile} />
        </ScrollReveal>
      </div>
    </PageShell>
  );
}

export default WalletPage;
